import React, { useEffect, useRef, useState, useMemo } from "react";
import Highcharts from "highcharts";

// Highcharts v12 CJS/ESM interop — module may be the function or wrap it in .default
(() => {
  try {
    const mod = require("highcharts/modules/map"); // eslint-disable-line global-require
    const fn  = mod?.default ?? mod;
    if (typeof fn === "function") fn(Highcharts);
  } catch (_) {}
})();

// Normalize common country name variations → Highcharts topology name
const NAME_MAP = {
  "usa": "United States of America",
  "us": "United States of America",
  "u.s.": "United States of America",
  "u.s.a.": "United States of America",
  "united states": "United States of America",
  "america": "United States of America",
  "uk": "United Kingdom",
  "u.k.": "United Kingdom",
  "great britain": "United Kingdom",
  "britain": "United Kingdom",
  "england": "United Kingdom",
  "scotland": "United Kingdom",
  "wales": "United Kingdom",
  "uae": "United Arab Emirates",
  "russia": "Russia",
  "russian federation": "Russia",
  "south korea": "Korea",
  "republic of korea": "Korea",
  "north korea": "North Korea",
  "dpr korea": "North Korea",
  "iran": "Iran",
  "islamic republic of iran": "Iran",
  "taiwan": "Taiwan",
  "czech republic": "Czechia",
  "czech": "Czechia",
  "vietnam": "Vietnam",
  "viet nam": "Vietnam",
  "republic of ireland": "Ireland",
  "syria": "Syria",
  "tanzania": "Tanzania",
  "bolivia": "Bolivia",
  "venezuela": "Venezuela",
  "moldova": "Moldova",
  "laos": "Laos",
  "myanmar": "Myanmar",
  "burma": "Myanmar",
  "palestine": "Palestine",
  "occupied palestinian territory": "Palestine",
};

const normalize = (name) => {
  if (!name) return "";
  const lower = name.toLowerCase().trim();
  return NAME_MAP[lower] ?? name.trim();
};

const TOPOLOGY_URL = "https://code.highcharts.com/mapdata/custom/world.topo.json";

// Distinct colors per tier — light → vibrant across the spectrum
const PALETTE = ["#86EFAC", "#FDE68A", "#FB923C", "#60A5FA", "#A78BFA", "#EF4444"];

const TIERS = [1, 10, 50, 100, 250, 500];

const buildDataClasses = (max) => {
  if (max <= 0) return [];
  return TIERS.map((from, i) => {
    const isLast = i === TIERS.length - 1;
    const to     = isLast ? null : TIERS[i + 1];
    return {
      from,
      ...(isLast ? {} : { to: to - 1 }),
      color: PALETTE[i],
      name:  isLast ? `${from}+` : `${from} – ${to - 1}`,
    };
  });
};

const WorldMapChart = ({ data, duration }) => {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const [topology, setTopology] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(false);

  const dataClasses = useMemo(() => {
    const max = Math.max(...(data ?? []).map((d) => d.count ?? 0), 0);
    return buildDataClasses(max);
  }, [data]);

  // Fetch world topology once
  useEffect(() => {
    fetch(TOPOLOGY_URL)
      .then((r) => {
        if (!r.ok) throw new Error("map fetch failed");
        return r.json();
      })
      .then((t) => { setTopology(t); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  // Create chart when topology is ready
  useEffect(() => {
    if (!containerRef.current || !topology) return;

    const seriesData = (data ?? [])
      .filter((d) => d.country && d.country !== "N/A")
      .map((d) => ({ country: normalize(d.country), value: d.count }));

    chartRef.current = Highcharts.mapChart(containerRef.current, {
      chart: {
        map: topology,
        height: 440,
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
      },
      credits: { enabled: false },
      accessibility: { enabled: false },
      title: { text: null },
      // Bias the projected map toward the left edge (instead of being centered
      // in the wide container) so it lines up with the left-aligned legend.
      mapView: {
        padding: [0, "18%", 0, 0],
      },
      mapNavigation: {
        enabled: true,
        enableDoubleClickZoom: true,
        enableMouseWheelZoom: false,
        buttonOptions: {
          align: "right",
          verticalAlign: "top",
          y: -4,
          theme: {
            fill: "#fff",
            "stroke-width": 1,
            stroke: "#e5e7eb",
            r: 6,
            padding: 5,
            style: { fontSize: "16px", color: "#374151" },
            states: {
              hover:  { fill: "#EEF2FF", stroke: "#0F40AE" },
              select: { fill: "#EEF2FF" },
            },
          },
        },
      },
      colorAxis: {
        dataClasses,
      },
      legend: {
        enabled: true,
        align: "left",
        verticalAlign: "bottom",
        layout: "horizontal",
        // Not floating -> the legend reserves its own row beneath the map
        // instead of being drawn on top of it.
        floating: false,
        padding: 8,
        margin: 12,
        title: {
          text: "Work Orders",
          style: { fontSize: "10px", color: "#888", fontWeight: "normal" },
        },
        itemStyle: { fontSize: "10px", color: "#555" },
        itemDistance: 12,
        symbolRadius: 3,
        symbolHeight: 10,
        symbolWidth: 10,
      },
      tooltip: {
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc" },
        borderWidth: 0,
        borderRadius: 6,
        formatter() {
          const v = this.point.value;
          if (!v) return `<b>${this.point.name}</b><br/><span style="color:#94a3b8">No work orders</span>`;
          return `<b>${this.point.name}</b><br/>${v.toLocaleString()} work order${v !== 1 ? "s" : ""}`;
        },
      },
      series: [
        {
          type: "map",
          name: "Work Orders",
          joinBy: ["name", "country"],
          data: seriesData,
          nullColor: "#f1f5f9",
          borderColor: "#d1d5db",
          borderWidth: 0.4,
          states: {
            hover: { color: "#FF5F00", borderColor: "#c2410c" },
          },
          dataLabels: {
            enabled: true,
            // Hide labels that would collide instead of stacking them on top of
            // each other in dense regions (Europe, Gulf states, Caribbean).
            // Hidden labels reappear as the user zooms in.
            allowOverlap: false,
            crop: true,
            overflow: "justify",
            padding: 2,
            backgroundColor: "none",
            borderWidth: 0,
            shadow: false,
            formatter() {
              return this.point.value ? this.point.value.toLocaleString() : null;
            },
            style: {
              fontSize: "9px",
              fontWeight: "700",
              textOutline: "2px rgba(255,255,255,0.8)",
              color: "#1e293b",
            },
          },
        },
      ],
    });

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topology]);

  // Update data + color classes when data changes (after chart exists)
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.update({ colorAxis: { dataClasses } }, false);
    chartRef.current.series[0].setData(
      (data ?? [])
        .filter((d) => d.country && d.country !== "N/A")
        .map((d) => ({ country: normalize(d.country), value: d.count })),
      true,
    );
  }, [data, dataClasses]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-40 text-gray-400">
        <i className="fa-solid fa-globe text-3xl mb-2" />
        <p className="text-sm">Map unavailable — check your internet connection</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm gap-2">
        <i className="fa-solid fa-spinner fa-spin" />
        Loading world map…
      </div>
    );
  }

  return <div ref={containerRef} className="w-full" />;
};

export default WorldMapChart;
